<template>
  <main
    class="bg-about-background bg-top pb-20 bg-[length:130rem] h-full bg-no-repeat bg-white pt-28"
  >
    <section class="mx-auto max-w-4xl pb-56">
      <div class="mb-6 flex flex-col gap-2">
        <div class="flex items-center gap-2 text-sm font-medium text-gray-500">
          <AcademicCapIcon class="h-4 w-4 text-primary" />
          Academy
        </div>
        <h2 class="text-gray-900 text-3xl font-semibold">
          Your Learning
        </h2>
        <p class="text-gray-600">
          Track your progress and continue your courses.
        </p>
      </div>
      <div
        v-if="courses.length"
        class="mt-6 grid gap-4"
      >
        <div
          v-for="course in courses"
          :key="course._id"
          class="bg-white p-6 border border-gray-200 shadow-sm rounded-2xl"
        >
          <div class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 class="text-gray-900 text-lg font-semibold">
                {{ course.title }}
              </h2>
              <div class="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
                  <Squares2X2Icon class="h-3.5 w-3.5" />
                  {{ course.modules.length }} Modules
                </span>
                <span class="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
                  <PlayCircleIcon class="h-3.5 w-3.5" />
                  {{ getTotalVideos(course) }} Videos
                </span>
              </div>
            </div>
            <div>
              <button
                v-if="!isEnrolled(course._id)"
                class="inline-flex items-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-[#c9ad99]"
                @click="enrollCourse(course._id)"
              >
                Start
                <ArrowRightIcon class="ml-2 h-4 w-4" />
              </button>
              <router-link
                v-else
                :to="`/dashboard/course/${course._id}`"
                class="inline-flex items-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-[#c9ad99]"
              >
                Continue Course
                <ArrowRightIcon class="ml-2 h-4 w-4" />
              </router-link>
            </div>
          </div>
          <div class="mt-4 mb-2 flex justify-between items-center">
            <h3 class="text-sm font-medium text-gray-700">
              Progress
            </h3>
            <span class="text-sm text-gray-700">
              {{ computeCourseProgress(course) }}% Complete
            </span>
          </div>
          <div
            :aria-valuenow="computeCourseProgress(course)"
            aria-valuemax="100"
            aria-valuemin="0"
            class="flex w-full h-1 rounded-full overflow-hidden bg-gray-200"
            role="progressbar"
          >
            <div
              :style="{ width: computeCourseProgress(course) + '%' }"
              class="flex flex-col justify-center overflow-hidden bg-primary text-xs text-white text-center whitespace-nowrap transition duration-500"
            />
          </div>
        </div>
      </div>
      <div
        v-else
        class="mt-6 text-sm text-gray-500"
      >
        No courses available.
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import {defineComponent, inject, onMounted, ref, watchEffect} from 'vue';
import axios from 'axios';
import {Course, User} from '@/types';
import {settingsKey} from "@/composables/useSettings";
import {useHead} from "@vueuse/head";
import {userKey} from '@/composables/useUser';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  PlayCircleIcon,
  Squares2X2Icon,
} from "@heroicons/vue/24/outline";

export default defineComponent({
  name: "Academy",
  components: {
    AcademicCapIcon,
    ArrowRightIcon,
    PlayCircleIcon,
    Squares2X2Icon,
  },
  setup() {
    const {settings} = inject(settingsKey)!;
    const {user} = inject(userKey)!;

    const courses = ref<Course[]>([]);
    const loading = ref<boolean>(true);
    const error = ref<string | null>(null);

    const fetchCourses = async (): Promise<void> => {
      try {
        const response = await axios.get<Course[]>('/course/all');
        courses.value = response.data;
      } catch (err) {
        error.value = "An error occurred while loading the courses!";
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const getTotalVideos = (course: Course): number => {
      return course.modules.reduce((total, module) => total + module.contents.length, 0);
    };

    const computeCourseProgress = (course: Course): number => {
      let total = 0;
      let completed = 0;
      course.modules.forEach((module) => {
        if (Array.isArray(module.contents)) {
          total += module.contents.length;
          completed += module.contents.filter((content) => content.completed).length;
        }
      });
      return total > 0 ? Math.round((completed / total) * 100) : 0;
    };

    const isEnrolled = (courseId: string) => {
      return (user.value?.enrolledCourses ?? []).some(id => id === courseId);
    };

    const enrollCourse = async (courseId: string): Promise<void> => {
      try {
        const response = await axios.post<{ user: User }>(`/course/enroll/${courseId}`);
        if (user?.value) {
          user.value.enrolledCourses = response.data.user.enrolledCourses;
        }
        alert('Successfully enrolled!');
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          alert(`Enrollment failed: ${error.response?.data?.message}`);
        } else {
          console.error('Enrollment error:', error);
          alert('Network error - please try again');
        }
      }
    };

    onMounted(async () => {
      await fetchCourses();
    });

    watchEffect(() => {
      if (settings.value) {
        useHead({
          title: 'Academy',
          meta: [
            {name: 'description', content: '...'},
            {name: 'keywords', content: '...'},
            {name: 'author', content: '...'},
          ],
        });
      }
    });

    return {
      courses,
      loading,
      error,
      getTotalVideos,
      computeCourseProgress,
      isEnrolled,
      enrollCourse,
    };
  },
});
</script>
